<script lang="ts" setup>
import { type VNodeChild } from 'vue';
import { IconCircleArrowUpFilled, IconCircleArrowDownFilled } from '@/icons';
import { hasSlotContent } from '../../utils';
interface Props {
  size?: 'xs' | 's' | 'm';
  diffType?: 'positive' | 'negative';
}
const { diffType = 'positive', size = 'm' } = defineProps<Props>();
defineSlots<{
  header?: () => VNodeChild;
  default?: () => VNodeChild;
  meta?: () => VNodeChild;
  diff?: () => VNodeChild;
  diffSuffix?: () => VNodeChild;
}>();
</script>

<template>
  <div
    :class="[
      $style.root,
      {
        [$style.sizeXs]: size === 'xs',
        [$style.sizeS]: size === 's',
        [$style.sizeM]: size === 'm',
      },
    ]"
  >
    <div :class="$style.main">
      <div v-if="hasSlotContent($slots.header)" :class="$style.heading">
        <slot name="header" />
      </div>
      <div v-if="hasSlotContent($slots.default)" :class="$style.mainText">
        <slot />
      </div>
    </div>
    <div v-if="hasSlotContent($slots.meta) || hasSlotContent($slots.diff)" :class="$style.bottom">
      <div v-if="hasSlotContent($slots.meta)" :class="$style.meta">
        <slot name="meta" />
      </div>
      <div
        v-if="hasSlotContent($slots.diff)"
        :class="[
          $style.diff,
          {
            [$style.diffPositive]: diffType === 'positive',
            [$style.diffNegative]: diffType === 'negative',
          },
        ]"
      >
        <div :class="$style.diffIcon">
          <IconCircleArrowUpFilled v-if="diffType === 'positive'" />
          <IconCircleArrowDownFilled v-else-if="diffType === 'negative'" />
        </div>
        <div>
          <div :class="$style.diffValue">
            <slot name="diff" />
          </div>
        </div>
        <div v-if="hasSlotContent($slots.diffSuffix)" :class="$style.diffSuffix">
          <slot name="diffSuffix" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
@use '../../styles/typography';

.root {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background: var(--ui2-background-primary, #fff);
  border: 1px solid var(--ui2-stroke-secondary, #e4e5e8);
  border-radius: 12px;
  padding: 12px;
  color: var(--ui2-content-primary, #010000);
  font-family: 'Poppins', sans-serif;
  overflow: hidden;
  width: 100%;
}

.sizeXs {
  gap: 8px;
  min-height: 56px;
  padding: 8px;
  border-radius: 10px;
}

.sizeS {
  gap: 10px;
  min-height: 80px;
}

.sizeM {
  gap: 20px;
  min-height: 100px;
}

.main {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sizeXs .main {
  gap: 4px;
}

.sizeS .main {
  gap: 4px;
}

.sizeM .main {
  gap: 8px;
}

.heading {
  font-size: 13px;
  line-height: 16px;
  font-weight: 500;
}

.sizeXs .heading {
  @include typography.style('data-2xs-medium');
  color: var(--ui2-content-tertiary, #978f91);
}

.mainText {
  font-weight: 700;
  font-size: 20px;
  line-height: 24px;
}

.sizeXs .mainText {
  @include typography.style('data-s-semibold');
  letter-spacing: -0.26px;
}

.sizeS .mainText {
  font-size: 16px;
  line-height: 20px;
}

.sizeM .mainText {
  font-size: 22px;
  line-height: 24px;
}

.meta {
  color: #978f91;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
}

.bottom {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.diff {
  display: flex;
  gap: 4px;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
}

.diffIcon {
  width: 16px;
  height: 16px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;

  > img,
  > svg {
    max-width: 100%;
    max-height: 100%;
  }
}

.diffValue {
  font-weight: 500;
  font-size: 13px;
  line-height: 16px;
}

.diffSuffix {
  color: #978f91;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
}

.diffPositive {
  & .diffValue,
  & .diffIcon {
    color: #00906e;
  }
}

.diffNegative {
  & .diffValue,
  & .diffIcon {
    color: #e63946;
  }
}
</style>
